// Trash — the company's soft-deleted entities (form templates, reports,
// inspections, tasks) awaiting restore or their automatic purge. Listed via
// POST trash/list with server-side pagination and the confirmed filters:
// deletedBy (company-user id array) and whenDeleted (preset enum; `custom`
// carries fromDate/toDate). Rows auto-purge TRASH_RETENTION_DAYS after
// deletion; `purgeInDays` is derived client-side from deletedAt so the table
// can show the countdown without a second wire field.
//
// `groupId` ties together every row of ONE deletion flow (e.g. a deleted
// project's tasks/inspections/reports cascade) — same id across the group.

import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';

/** Numeric item-type codes, verbatim from the backend's TrashItemType enum. */
export enum TrashItemType {
  FORM_TEMPLATE = 1,
  REPORT = 2,
  INSPECTION = 3,
  TASK = 4,
  PROJECT = 5,
  DATA_SOURCE = 6,
}

/** "When deleted" filter slugs, verbatim from the backend's
 *  TrashDeletedFilter enum. NOTE the ids differ from the forms-domain
 *  FormDateFilter ('last7days' here vs 'last_7_days' there), so the trash page
 *  builds its own preset options instead of reusing buildDateFilterOptions. */
export enum TrashDeletedFilter {
  ANYTIME = 'anytime',
  TODAY = 'today',
  LAST_7_DAYS = 'last7days',
  LAST_30_DAYS = 'last30days',
  CUSTOM = 'custom',
}

/** Days a row stays restorable before its automatic permanent deletion —
 *  mirrors the backend's TRASH_RETENTION_DAYS. */
export const TRASH_RETENTION_DAYS = 30;

export interface TrashActor {
  id: string;
  fullName: string;
}

export interface TrashRow {
  id: string;
  itemType: TrashItemType;
  /** Display label captured at trash time (null for a bare legacy entity —
   *  the cell falls back to the referenced document's fields, then dashes). */
  title: string | null;
  /** The referenced (soft-deleted) entity's own reference number, when its
   *  populated document carries one. */
  referenceNumber?: number;
  /** Null when the deleter's name is blank — the cell dashes. */
  deletedBy: TrashActor | null;
  /** ISO — when the entity was trashed. */
  deletedAt: string;
  /** Same id across every row created by one deletion flow; null on rows
   *  written before the field existed. */
  groupId: string | null;
  /** Whole days until the automatic purge (retention 30 days), floored at 0. */
  purgeInDays: number;
}

export interface TrashPage {
  items: TrashRow[];
  /** Only reported on page-1 responses — absent while paging deeper. */
  total?: number;
}

/** POST trash/list body — every optional param is omitted (undefined) when
 *  unset, which JSON.stringify drops. */
export interface TrashQueryArgs {
  /** Company-user ids whose deletions to show. */
  deletedBy?: string[];
  /** Deleted-date preset — omitted for "Any time". */
  whenDeleted?: TrashDeletedFilter;
  /** ISO datetime edges, sent only while whenDeleted === 'custom'. */
  fromDate?: string;
  toDate?: string;
  /** Days-left windows (1..30) — each value selects rows purging that day. */
  purged?: number[];
  page: number;
  limit: number;
}

// ---- raw shapes (per the trash/list response) ----
interface ApiTrashActor {
  _id: string;
  user?: { _id: string; firstName?: string; lastName?: string } | null;
}
interface ApiTrashRow {
  _id: string;
  itemType: TrashItemType;
  itemTitle?: string | null;
  /** The referenced soft-deleted document (populated withDeleted); null when
   *  it no longer exists at all. */
  item?: { _id: string; title?: string; referenceNumber?: number } | null;
  deletedBy?: ApiTrashActor | null;
  deletedAt: string;
  groupId?: string | null;
}
interface ApiTrashPage {
  items: ApiTrashRow[];
  totalCount?: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;

// Same nested { user: { firstName, lastName } } actor shape as the activity
// log's. A blank name is null, so the cell dashes instead of a lone space.
const toActor = (p?: ApiTrashActor | null): TrashActor | null => {
  const fullName = `${p?.user?.firstName ?? ''} ${p?.user?.lastName ?? ''}`.trim();
  return p && fullName ? { id: p._id, fullName } : null;
};

// A row purges at deletedAt + retention; the countdown rounds UP (a row less
// than a day out still reads "1 day left") and floors at 0 once overdue.
const purgeInDays = (deletedAt: string): number => {
  const deleted = new Date(deletedAt).getTime();
  if (Number.isNaN(deleted)) return 0;
  const msLeft = deleted + TRASH_RETENTION_DAYS * DAY_MS - Date.now();
  return Math.max(0, Math.ceil(msLeft / DAY_MS));
};

const toTrashRow = (r: ApiTrashRow): TrashRow => ({
  id: r._id,
  itemType: r.itemType,
  // The trash-time label wins; a bare legacy row falls back to the populated
  // document's own title (reports carry a referenceNumber instead).
  title: r.itemTitle ?? r.item?.title ?? null,
  referenceNumber: r.item?.referenceNumber,
  deletedBy: toActor(r.deletedBy),
  deletedAt: r.deletedAt,
  groupId: r.groupId ?? null,
  purgeInDays: purgeInDays(r.deletedAt),
});

/** POST trash/bulk-restore | trash/bulk-delete body — the acted-on trash ROW
 *  ids (TrashRow.id), not the referenced entities'. Both are all-or-nothing:
 *  restore rejects the whole batch when a row's parent is still deleted (the
 *  server's message says which and what to do), so surface `msg` over the
 *  generic toast. */
export interface BulkTrashIdsPayload {
  ids: string[];
}

export const trashApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getTrashList: build.query<TrashPage, TrashQueryArgs>({
      query: (body) => ({ ...ROUTES.trash.list, data: body }),
      transformResponse: (res: ApiTrashPage) => ({
        items: res.items.map(toTrashRow),
        total: res.totalCount,
      }),
      providesTags: [{ type: 'Trash', id: 'LIST' }],
    }),
    // Both actions remove their rows from the listing (restored items go back
    // to their modules, permanently deleted ones are gone for good), so each
    // must match what the list PROVIDES for it to refetch.
    bulkRestoreTrash: build.mutation<unknown, BulkTrashIdsPayload>({
      query: (body) => ({ ...ROUTES.trash.bulkRestore, data: body }),
      invalidatesTags: [{ type: 'Trash', id: 'LIST' }],
    }),
    bulkDeleteTrash: build.mutation<unknown, BulkTrashIdsPayload>({
      query: (body) => ({ ...ROUTES.trash.bulkDelete, data: body }),
      invalidatesTags: [{ type: 'Trash', id: 'LIST' }],
    }),
  }),
});

export const { useGetTrashListQuery, useBulkRestoreTrashMutation, useBulkDeleteTrashMutation } =
  trashApi;
